/**
 * Kingdom Factor - Our Calling Page
 * - Hero with 2 Timothy 2:2 quote
 * - Intro section with calling, passion, model narrative
 * - Leadership team section with circular headshots
 * - Coach Advisory Council section
 * - View All Member Stories link
 * - CTA section
 */

import { useState } from "react"
import { Link } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { GetStartedDialog } from "@/components/GetStartedDialog"

function Headshot({ src, name, size = "w-36 h-36", textSize = "text-4xl", textColor = "text-zinc-400" }) {
  const [failed, setFailed] = useState(false)
  const initials = name.split(" ").map(n => n[0]).join("")

  if (!src || failed) {
    return (
      <span className={`flex items-center justify-center w-full h-full ${textSize} font-display ${textColor}`}>
        {initials}
      </span>
    )
  }

  return <img src={src} alt={name} className="w-full h-full object-cover" onError={() => setFailed(true)} />
}

// R2 public URL for marketing assets
const R2_BASE_URL = "https://pub-d0ab0b104ce14000869f88e713e41bb8.r2.dev"

const leadershipTeam = [
  {
    name: "Mark Clevenger",
    title: "Leader",
    image: `${R2_BASE_URL}/marketing_assets/mark_clevenger_headshot.webp`,
  },
  {
    name: "Jim Lange",
    title: "Leader/Coach",
    image: `${R2_BASE_URL}/marketing_assets/jim_lange_headshot.webp`,
  },
  {
    name: "Ray Hilbert",
    title: "Leader/Coach",
    image: `${R2_BASE_URL}/marketing_assets/ray_hilbert_headshot.webp`,
  },
  {
    name: "Jen Marr-Burkholder",
    title: "Leader",
    image: `${R2_BASE_URL}/marketing_assets/jen_marr_burkholder_headshot.webp`,
  },
]

const advisoryCouncil = [
  { name: "Mun Wai Chung", image: `${R2_BASE_URL}/marketing_assets/mun_wai_chung_headshot.webp` },
  { name: "Jana Cardona", image: `${R2_BASE_URL}/marketing_assets/jana_cardona_headshot.webp` },
  { name: "David Moody", image: `${R2_BASE_URL}/marketing_assets/david_moody_headshot.webp` },
  { name: "Mark Queen", image: `${R2_BASE_URL}/marketing_assets/mark_queen_headshot.webp` },
  { name: "Tim Harrison", image: `${R2_BASE_URL}/marketing_assets/tim_harrison_headshot.webp` },
  { name: "James Justice", image: `${R2_BASE_URL}/marketing_assets/james_justice_headshot.webp` },
]

export default function OurCalling({ seo, settings = {}, coachArticleLinks = {} }) {
  const { scheduleCallUrl = "#" } = settings
  const [getStartedOpen, setGetStartedOpen] = useState(false)

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="our-calling" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero Section */}
        <section className="relative bg-kf-gradient pt-32 pb-20 overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-8">
              KINGDOM FACTOR CALLING
            </h1>
            <blockquote className="text-white/90 text-lg md:text-xl leading-relaxed italic max-w-3xl mx-auto">
              "And the things that you have heard from me among many witnesses, commit these to faithful leaders who will be able to teach others also."
              <cite className="block mt-4 not-italic text-white font-semibold">— 2 Timothy 2:2</cite>
            </blockquote>
          </div>
        </section>

        {/* Intro Section */}
        <section className="bg-zinc-50 py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-zinc-700 text-lg leading-relaxed mb-6">
                Kingdom Factor leadership brings over 60 years of combined experience helping Christian business leaders become who and what God has called them to be. Our <strong className="text-kf-red">calling</strong> is building God's Kingdom in and through the marketplace. Our <strong className="text-kf-red">passion</strong> is developing godly business leaders who are being transformed by Christ. Our <strong className="text-kf-red">model</strong> is focused on developing <strong className="text-kf-red">Coaches</strong> who are equipped to be a <em className="font-semibold">leader of leaders</em>.
              </p>
              <p className="text-zinc-600 text-lg leading-relaxed">
                We've worn all the hats as business owners, coaches, and developers of successful coaching strategies and content for Christian business leaders. We understand marketplace ministry and what it means to lead a company rooted in Biblical principles. Living both sides of the coaching equation means we know what works, what doesn't, and we know how to discern practical innovations from among all the options. The Kingdom Factor Leadership Model equips exceptional Christian business coaches to lead and serve their Cohort Members.
              </p>
            </div>
          </div>
        </section>

        {/* Leadership Team Section */}
        <section className="bg-white py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-3xl md:text-4xl text-zinc-900 text-center mb-4">
              WE HAVE BEEN IN <span className="text-kf-red">YOUR SEAT</span>
            </h2>
            <p className="text-zinc-600 text-center mb-12 max-w-2xl mx-auto">
              Our leadership team has invested over 60 years and more than 80,000 hours collectively in coaching and equipping thousands of Christian business owners and leaders — making us among the most experienced and qualified team in the country to provide council members an exceptional experience.
            </p>

            <div className="flex flex-wrap justify-center gap-8 lg:gap-10">
              {leadershipTeam.map((member, index) => (
                <div key={index} className="text-center w-40">
                  <div className="w-36 h-36 mx-auto mb-4 rounded-full overflow-hidden shadow-lg ring-4 ring-white bg-zinc-200">
                    <Headshot src={member.image} name={member.name} />
                  </div>
                  <h3 className="font-display text-lg text-zinc-900 mb-1">{member.name}</h3>
                  <p className="text-kf-red font-medium text-sm mb-2">{member.title}</p>
                  {coachArticleLinks[member.name] && (
                    <Link
                      href={coachArticleLinks[member.name]}
                      className="text-kf-red hover:underline text-xs font-medium mt-2 inline-flex items-center gap-1"
                    >
                      View Story
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* Coach Advisory Council */}
        <section className="bg-zinc-50 py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-kf-red font-semibold uppercase tracking-wider mb-3">
              Coach Advisory Council
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-zinc-900 mb-6">
              KINGDOM FACTOR COACH ADVISORY COUNCIL
            </h2>
            <p className="text-zinc-600 max-w-3xl mx-auto mb-10 text-lg leading-relaxed">
              The Kingdom Factor Coach Advisory Council is a select group of trusted
              KF Coaches aligned with our mission and values and committed to
              strengthening the Kingdom Factor Leadership Model. These leaders provide
              strategic insight, practical feedback, and field-tested perspective as
              we develop new initiatives and resources for our coaches and the business
              leaders they serve. The Council represents the voice of our coaching
              community and helps ensure everything we build advances our shared
              Kingdom impact.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
              {advisoryCouncil.map((member) => (
                <div key={member.name} className="flex flex-col items-center">
                  <div className="w-24 h-24 rounded-full bg-zinc-200 overflow-hidden mb-4">
                    <Headshot src={member.image} name={member.name} textSize="text-2xl" textColor="text-zinc-500" />
                  </div>
                  <p className="font-semibold text-zinc-900">{member.name}</p>
                  {coachArticleLinks[member.name] && (
                    <Link
                      href={coachArticleLinks[member.name]}
                      className="text-kf-red hover:underline text-xs font-medium mt-1 inline-flex items-center gap-1"
                    >
                      View Story
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* View All Member Stories */}
        <div className="text-center py-12 bg-white">
          <Button variant="gradient" asChild>
            <Link href="/collective/kf-member-stories">
              View All Member Stories
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>

        {/* CTA Section */}
        <section className="bg-kf-gradient py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
              READY TO START YOUR JOURNEY?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Whether you're looking to become a KF Coach or join as a Member, we'd love to talk with you about how Kingdom Factor can help you grow.
            </p>
            <Button variant="kf-secondary" size="lg" className="group" onClick={() => setGetStartedOpen(true)}>
              Get Started
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </section>

        <Footer activePage="our-calling" />
      </div>

      <GetStartedDialog open={getStartedOpen} onOpenChange={setGetStartedOpen} />
    </>
  )
}
